import { memo, useMemo } from "react";
import {
  PREVIEW_ROW_COUNT,
  generatePreviewCell,
} from "constants/crmFieldExportPreview";
import { CrmFilterField } from "types/Models/crmFields";

type CrmExportPreviewTableProps = {
  fields: CrmFilterField[];
};

const PREVIEW_ROW_INDEXES = Array.from(
  { length: PREVIEW_ROW_COUNT },
  (_, i) => i
);

/**
 * Illustrative "what your export will look like" preview - memoized so typing in the Available
 * search box or opening the template dropdown never re-renders this table; it only depends on
 * which fields are picked and in what order.
 */
export const CrmExportPreviewTable = memo(
  ({ fields }: CrmExportPreviewTableProps) => {
    const rows = useMemo(
      () =>
        PREVIEW_ROW_INDEXES.map((rowIndex) =>
          fields.map((field) => generatePreviewCell(field, rowIndex))
        ),
      [fields]
    );

    if (fields.length === 0) {
      return (
        <div className="t-flex t-h-full t-items-center t-justify-center t-px-4 t-text-center t-text-body-sm t-text-text-30">
          Add a field on the right to see it appear here.
        </div>
      );
    }

    return (
      <div className="t-h-full t-overflow-auto">
        <table className="t-w-full t-border-separate t-border-spacing-0 t-text-body-sm">
          <thead>
            <tr>
              {fields.map((field) => (
                <th
                  key={field.field_key}
                  className="t-sticky t-top-0 t-z-10 t-whitespace-nowrap t-border-0 t-border-b t-border-solid t-border-neutral-10 t-bg-surface t-px-3 t-py-2 t-text-left t-text-body-sm t-font-semibold t-text-text-60"
                >
                  {field.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={rowIndex} className="hover:t-bg-surface-lighter-grey">
                {row.map((cell, cellIndex) => (
                  <td
                    // eslint-disable-next-line react/no-array-index-key
                    key={cellIndex}
                    className="t-whitespace-nowrap t-border-0 t-border-b t-border-solid t-border-neutral-0 t-px-3 t-py-2 t-text-text-60"
                  >
                    {cell || <span className="t-text-text-30">—</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
);

CrmExportPreviewTable.displayName = "CrmExportPreviewTable";
